import * as _ from 'lodash'
import valueFilters from '../valueFilters';
import validator from '../validator';
import modelFactory, { IModel } from '../modelFactory';
import contactModel, { IContact } from './contactModel';
import addressModel, { IAddress } from './addressModel';
import contractorNoteModel, { IContractorNote } from './contractorNoteModel';
import contractorAdditionalInfoModel, { IContractorAdditionalInfo } from './contractorAdditionalInfoModel';
import contractorUpgradeModel, { IContractorUpgrade } from './contractorUpgradeModel';
import phonable, { IPhonable } from './phonable';
import statusLogged, { IStatusLogged } from './statusLogged';
import model from './model';
import enums from '../enum';
import { IModelSet } from 'Scripts/modelSet';
import additionalInfoContainer, { IAdditionalInfoContainer } from './additionalInfoContainer';

export interface IContractor extends IModel, IPhonable, IStatusLogged, IAdditionalInfoContainer<IContractorAdditionalInfo> {
	type: number;
	name?: string;
	accountingId?: string;
	// additionalInfo: IModelSet<IContractorAdditionalInfo>;
	defaultSubTradeTypeKey?: string;
	isTaxExempt: boolean;
	contacts?: IModelSet<IContact>;
	addresses?: IModelSet<IAddress>;
	notes?: IModelSet<IContractorNote>;
	upgrades: IModelSet<IContractorUpgrade>;
	jobCostKeys: string[];
	paymentTermsKey?: string;
	officeAddress?: IAddress;
	discountPercent?: number;
	primaryContact?: IContact;
	readonly primaryContactName: string;
}

const contractorFactory = modelFactory.createFactory<IContractor>('Contractor', _.assign({}, model, phonable, statusLogged('active'), additionalInfoContainer, {
	type: {
		setFilters: valueFilters.toNumber,
		defaultValue: 0,
		rules: validator.enum('contractorTypes', true) // disallow unknown
	},
	name: {
		rules: validator.required()
	},
	accountingId: {
		setFilters: valueFilters.clean
	},
	additionalInfo: {
		defaultValue: [],
		setFilters: valueFilters.toModelSet(contractorAdditionalInfoModel),
		rules: validator.validModel(contractorAdditionalInfoModel.rules())
	},
	defaultSubTradeTypeKey: {
		setFilters: valueFilters.clean
	},
	isTaxExempt: {
		setFilters: valueFilters.toBoolean,
		defaultValue: false
	},
	contacts: {
		setFilters: valueFilters.toModelSet(contactModel),
		rules: validator.validModel(contactModel.rules())
	},
	addresses: {
		setFilters: valueFilters.toModelSet(addressModel),
		rules: validator.validModel(addressModel.rules())
	},
	notes: {
		setFilters: valueFilters.toModelSet(contractorNoteModel),
		rules: validator.validModel(contractorNoteModel.rules())
	},
	upgrades: {
		defaultValue: [],
		setFilters: valueFilters.toModelSet(contractorUpgradeModel),
		rules: validator.validModel(contractorUpgradeModel.rules())
	},
	jobCostKeys: {
		defaultValue: []
	},
	paymentTermsKey: {
		setFilters: valueFilters.clean
	},
	primaryContact: {
		nonSerialized: true
	},
	_get_primaryContact: function () {
		return this._getContactWithPriority(0);
	},
	_set_primaryContact: function (val) {
		this.setContactWithPriority(val, 0);
	},
	primaryContactName: {
		writable: false,
		nonSerialized: true
	},
	_get_primaryContactName: function () {
		return this.primaryContact ? this.primaryContact.fullName : '';
	},
	setContactWithPriority: function (contact, priority) {
		if (!this.contacts) {
			this.contacts = [];
		}

		const existing = _.find(this.contacts, c => c.priority === priority && !c.externalFormKey);

		if (existing) {
			if (!contact) {
				this.contacts.delete(existing.key);
			}
			else {
				_.assign(existing, contact, { priority: priority });
			}
		}
		else if (contact) {
			contact.priority = priority;
			this.contacts[contact.key] = contact;
		}
	},
	_getContactWithPriority: function (priority) {
		return _.find(this.contacts, c => c.priority === priority && !c.externalFormKey);
	},
	officeAddress: {
		nonSerialized: true
	},
	_set_officeAddress: function (val) {
		this.setAddressOfType(val, enums.addressTypes.Office);
	},
	_get_officeAddress: function () {
		const result = this.getAddressByType(enums.addressTypes.Office);

		return result ? result : undefined;
	},
	setAddressOfType: function (address, type) {
		if (!this.addresss) {
			this.addresses = [];
		}

		const existing = _.find(this.addresses, x => { return enums.addressTypes.isSet(type, x.type); });

		if (existing) {
			if (!address) {
				this.addresses.delete(existing.key);
			}
			else {
				_.assign(existing, address, { type: enums.addressTypes.set(type, address.type) });
			}
		}
		else if (address) {
			address.type = enums.addressTypes.set(type, address.type);
			this.addresses[address.key] = address;
		}
	},
	getAddressByType: function (type) {
		return _.find(this.addresses, x => {
			return enums.addressTypes.isSet(type, x.type);
		});
	},
	discountPercent: {
		nonSerialized: true,
		setFilters: valueFilters.toNumber
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the discountPercent property
	_get_discountPercent: function () {
		const stringVal = this.getAdditionalInfoValue('discountPercent', '0');
		return Number(stringVal);
	},
	_set_discountPercent: function (val) {
		this.setAdditionalInfo('discountPercent', val);
	},
	// getAdditionalInfo: function (name) {
	// 	if (!this.additionalInfo) { return; }

	// 	return _.find(this.additionalInfo, { name });
	// },
	// getAdditionalInfoValue: function (name, defaultValue = '') {
	// 	const info = this.getAdditionalInfo(name);

	// 	return info && info.value || defaultValue;
	// },
	// setAdditionalInfo: function (name, value) {
	// 	if (!this.additionalInfo) { return; }

	// 	const isUndefined = typeof value === 'undefined' || value === '';
	// 	let result = this.getAdditionalInfo(name);

	// 	if (!result) {
	// 		result = this.additionalInfo.add({ name });
	// 	}

	// 	if (result) {
	// 		if (isUndefined) {
	// 			this.additionalInfo.delete(result.key);
	// 			result = undefined;
	// 		}
	// 		else {
	// 			result.value = value;
	// 		}
	// 	}

	// 	return result;
	// }
}));

export default contractorFactory;